import { Tooltip, Badge } from "@mui/material";
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SanitizerIcon from '@mui/icons-material/Sanitizer';
import ShowerIcon from '@mui/icons-material/Shower';
import FlagIcon from '@mui/icons-material/Flag';
import { useState } from 'react';
import ServiceFlagTooltip from './GraphHelpers/ServiceFlagTooltip2';

// Define the ServiceFlag component for displaying service event icons and tooltips
interface ServiceFlagProps {
    viewBox?: { x?: number; y?: number; width?: number; height?: number };
    serviceEvents: Array<{
        dateServiced: string;
        id?: number | string;
        serviceType?: string;
        groupLabel?: string;
    }>;
}

const ServiceFlags: React.FC<ServiceFlagProps> = ({ viewBox, serviceEvents }) => {
    const [currentEventIndex, setCurrentEventIndex] = useState(0);

    if (!viewBox ||
        typeof viewBox.x === 'undefined' ||
        typeof viewBox.y === 'undefined' ||
        typeof viewBox.height === 'undefined' ||
        !serviceEvents ||
        serviceEvents.length === 0) {
        return null;
    }

    const { x: rChartX, y: rChartY, height: plotAreaHeight } = viewBox;
    const currentEvent = serviceEvents[currentEventIndex];

    const iconSize = 18;
    const yPositionInPlotArea = (rChartY + plotAreaHeight) - iconSize - 10;

    const iconFinder = function (serviceType?: string) {
        if (!serviceType) return <FlagIcon sx={{ color: '#7f8c8d' }} />;
        switch (serviceType.toLowerCase()) {
            case 'sheet': return <WarehouseIcon sx={{ color: '#940000' }} />
            case 'refrigeration': return <AcUnitIcon sx={{ color: '#0288d1' }} />
            case 'preseason': return <UndoIcon sx={{ color: '#00796b' }} />
            case 'inspections': return <SearchIcon sx={{ color: '#5d4037' }} />
            case 'chemappagreement': return <CloudSyncIcon sx={{ color: '#7b1fa2' }} />
            case 'disinfectantagreement': return <SanitizerIcon sx={{ color: '#689f38' }} />
            case 'spraybaragreement': return <ShowerIcon sx={{ color: '#0097a7' }} />
            case 'freshpack': return <PrecisionManufacturingIcon sx={{ color: '#388e3c' }} />
            case 'labs': return <AssessmentIcon sx={{ color: '#607d8b' }} />;
            default: return <FlagIcon sx={{ color: '#7f8c8d' }} />;
        }
    };

    const getServiceColor = (serviceType?: string) => {
        if (!serviceType) return '#7f8c8d';
        switch (serviceType.toLowerCase()) {
            case 'sheet': return '#940000'
            case 'refrigeration': return '#0288d1'
            case 'preseason': return '#00796b'
            case 'inspections': return '#5d4037'
            case 'chemappagreement': return '#7b1fa2'
            case 'disinfectantagreement': return '#689f38'
            case 'spraybaragreement': return '#0097a7'
            case 'freshpack': return '#388e3c'
            case 'labdata': return '#607d8b'
            default: return '#7f8c8d';
        }
    };

    const formatServiceType = (type?: string) => {
        if (!type) return "Service";
        const spacedType = type.replace(/([A-Z])/g, ' $1').trim();
        return spacedType.charAt(0).toUpperCase() + spacedType.slice(1);
    };

    const handlePrevious = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setCurrentEventIndex((prev) => (prev > 0 ? prev - 1 : serviceEvents.length - 1));
    };

    const handleNext = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setCurrentEventIndex((prev) => (prev < serviceEvents.length - 1 ? prev + 1 : 0));
    };

    const displayIcon = iconFinder(currentEvent.serviceType);

    return (
        <foreignObject
            x={rChartX - iconSize / 2}
            y={yPositionInPlotArea}
            width={iconSize}
            height={iconSize}
            style={{ overflow: 'visible', cursor: 'pointer' }}
        >
            <Tooltip
                title={
                    <ServiceFlagTooltip
                        serviceEvents={serviceEvents}
                        currentEventIndex={currentEventIndex}
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                        iconFinder={iconFinder}
                        formatServiceType={formatServiceType}
                        getServiceColor={getServiceColor}
                    />
                }
                arrow
                placement="top"
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                }}>
                    {serviceEvents.length > 1 ? (
                        <Badge
                            badgeContent={serviceEvents.length}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '0.6rem',
                                    height: 14,
                                    minWidth: 14,
                                    borderRadius: '50%',
                                }
                            }}
                        >
                            {displayIcon}
                        </Badge>
                    ) : (
                        displayIcon
                    )}
                </div>
            </Tooltip>
        </foreignObject>
    );
};

export default ServiceFlags;